import { useCallback, useEffect, useRef, useState } from "react"

import { useLazyQuery } from "@apollo/client"
import { v4 as uuidv4 } from "uuid"

import { ApolloContext, QueryTiming } from "@/provider/apollo/context"
import { graphql } from "@/public/gql/__generated__/gql"
import {
	GetSevenDaysReservableSlotsChunksQuery,
	GetSevenDaysReservableSlotsChunksV2Query,
} from "@/public/gql/__generated__/graphql"
import {
	formatJstDateUntilDayByHyphen,
	formatJstDateUntilMinute,
} from "@/utils/date"

const SevenDaysReservableSlotsChunks = graphql(/* GraphQL */ `
	query GetSevenDaysReservableSlotsChunks(
		$param: SevenDaysReservableSlotsChunksParam!
	) {
		sevenDaysReservableSlotsChunks(param: $param) {
			chunks {
				...dateReservableSlots
			}
		}
	}
`)

const SevenDaysReservableSlotsChunksV2 = graphql(/* GraphQL */ `
	query GetSevenDaysReservableSlotsChunksV2(
		$param: SevenDaysReservableSlotsChunksV2Param!
	) {
		sevenDaysReservableSlotsChunksV2(param: $param) {
			cacheReqId
			needAnyReservableEquipmentToCreateReservation
			needSameEquipmentAsPreviousToCreateReservation
			staggeredSchedulingEnabled
			chunks {
				...dateReservableSlotsV2
			}
			recommendedSlots {
				...recommendedSlot
			}
		}
	}
`)

const PAGE_VERSION_1 = 1
const PAGE_VERSION_2 = 2

export const DEFAULT_DURATION_MINUTE = 0

type ChunksV1ReturnType = {
	getChunksAndExtend: (fromDate: Date, cancelID?: string) => void
	chunks: GetSevenDaysReservableSlotsChunksQuery["sevenDaysReservableSlotsChunks"]["chunks"]
	isLoading: boolean
	sevenDaysFrom: Date
	setSevenDaysFrom: React.Dispatch<React.SetStateAction<Date>>
}

type ChunksV2ReturnType = {
	getChunksAndExtend: (fromDate: Date, cancelID?: string) => void
	recommendedSlots: GetSevenDaysReservableSlotsChunksV2Query["sevenDaysReservableSlotsChunksV2"]["recommendedSlots"]
	chunks: GetSevenDaysReservableSlotsChunksV2Query["sevenDaysReservableSlotsChunksV2"]["chunks"]
	needAnyReservableEquipmentToCreateReservation: boolean
	needSameEquipmentAsPreviousToCreateReservation: boolean
	staggeredSchedulingEnabled: boolean
	isLoading: boolean
	sevenDaysFrom: Date
	setSevenDaysFrom: React.Dispatch<React.SetStateAction<Date>>
	resetAndRefetch: (cancelID: string) => void
}

export const getMenuNameTextsWithPriceAndMinutes = (
	names: string[],
	price?: number,
	durationMinute?: number
) => {
	const texts: string[] = []
	texts.push(`【前回ご予約】 (${durationMinute}分) - ¥${price}`)
	for (const name of names) {
		texts.push(`・${name}`)
	}
	return texts
}

export const getMenuNameTexts = (names: string[]) => {
	const texts: string[] = []
	texts.push(`【前回ご予約】`)
	for (const name of names) {
		texts.push(`・${name}`)
	}
	return texts
}

export const buildConfirmMessageBase = (
	startTime: Date,
	menuAndCouponNames: string[],
	staffName: string
) => {
	let message = "以下の内容で予約します。よろしいですか？\n\n"
	message += `予約日時: ${formatJstDateUntilMinute(startTime)}\n`
	message += `指名スタッフ: ${staffName}\n`
	message += "\n"
	message += "メニュー:\n"
	for (const menuName of menuAndCouponNames) {
		message += `・${menuName}\n`
	}
	return message
}

export const getConfirmMessage = (
	startTime: Date,
	isMenuSelected: boolean,
	menuAndCouponNames: string[],
	staffName: string
) => {
	if (!isMenuSelected) {
		menuAndCouponNames = ["なし"]
	}
	return buildConfirmMessageBase(startTime, menuAndCouponNames, staffName)
}

// 型推論を十全に機能させるためにオーバーロードを使う
export function useChunks(
	version: 1,
	cancelID?: string,
	fetchedReservableSlots?: (
		startTime: Date,
		reqId: string,
		tookMilliseconds: number,
		needAnyReservableEquipmentToCreateReservation: boolean,
		needSameEquipmentAsPreviousToCreateReservation: boolean,
		staggeredSchedulingEnabled: boolean,
		cacheReqId?: string
	) => void
): ChunksV1ReturnType
export function useChunks(
	version: 2,
	cancelID?: string,
	fetchedReservableSlots?: (
		startTime: Date,
		reqId: string,
		tookMilliseconds: number,
		needAnyReservableEquipmentToCreateReservation: boolean,
		needSameEquipmentAsPreviousToCreateReservation: boolean,
		staggeredSchedulingEnabled: boolean,
		cacheReqId?: string
	) => void
): ChunksV2ReturnType
// 使用するクエリの型が異なるため、別々のカスタムフックをバージョンを引数に分岐して返す
export function useChunks(
	version: number,
	cancelID?: string,
	fetchedReservableSlots?: (
		startTime: Date,
		reqId: string,
		tookMilliseconds: number,
		needAnyReservableEquipmentToCreateReservation: boolean,
		needSameEquipmentAsPreviousToCreateReservation: boolean,
		staggeredSchedulingEnabled: boolean,
		cacheReqId?: string
	) => void
): ChunksV1ReturnType | ChunksV2ReturnType {
	if (version === PAGE_VERSION_1) {
		return useChunksV1(cancelID, fetchedReservableSlots)
	} else if (version === PAGE_VERSION_2) {
		return useChunksV2(cancelID, fetchedReservableSlots)
	} else {
		throw new Error("Invalid version")
	}
}

const useChunksV1 = (
	cancelID?: string,
	fetchedReservableSlots?: (
		startTime: Date,
		reqId: string,
		tookMilliseconds: number,
		needAnyReservableEquipmentToCreateReservation: boolean,
		needSameEquipmentAsPreviousToCreateReservation: boolean,
		staggeredSchedulingEnabled: boolean,
		cacheReqId?: string
	) => void
): ChunksV1ReturnType => {
	const [sevenDaysFrom, setSevenDaysFrom] = useState(new Date())

	// v1 では fetchedReservableSlots イベントを飛ばさない
	const queryTimingCallback = useCallback((timing: QueryTiming) => {
		// Do nothing for v1
	}, [])

	const [
		getSevenDaysReservableSlotsChunks,
		getSevenDaysReservableSlotsChunksResult,
	] = useLazyQuery(SevenDaysReservableSlotsChunks, {
		fetchPolicy: "no-cache",
	})
	const [chunks, setChunks] = useState<
		GetSevenDaysReservableSlotsChunksQuery["sevenDaysReservableSlotsChunks"]["chunks"]
	>([])
	// fromDate を管理する Set。呼び出し中 or 既に完了した fromDate を記録する. (loadingとかstate使うと更新が遅れて複数回呼ばれてしまう)
	const fetchingFromDatesRef = useRef<Set<string>>(new Set())
	// すでに読み込み中 or 読み込み済みの fromDate かどうかをチェックする関数
	const canFetch = useCallback((fromDateStr: string) => {
		return !fetchingFromDatesRef.current.has(fromDateStr)
	}, [])

	// fromDate に対して一度しかクエリを投げないようにする関数
	const getChunksAndExtend = useCallback(
		(fromDate: Date, cancelID?: string) => {
			if (!cancelID) return

			const fromDateStr = formatJstDateUntilDayByHyphen(fromDate)

			if (!canFetch(fromDateStr)) return

			fetchingFromDatesRef.current.add(fromDateStr)

			const context: ApolloContext = {
				fetchOptions: {
					requestId: uuidv4(),
					skipLoading: true,
				},
				queryTimingCallback,
			}

			getSevenDaysReservableSlotsChunks({
				variables: { param: { fromDate: fromDateStr, cancelId: cancelID } },
				context,
			})
				.then((res) => {
					const gotChunks =
						res.data?.sevenDaysReservableSlotsChunks.chunks ?? []
					setChunks((prev) => [...prev, ...gotChunks])
				})
				.catch((error) => {
					fetchingFromDatesRef.current.delete(fromDateStr)
					console.error(error)
					throw error
				})
		},
		[canFetch, getSevenDaysReservableSlotsChunks]
	)

	useEffect(() => {
		getChunksAndExtend(sevenDaysFrom, cancelID)
	}, [cancelID, getChunksAndExtend, sevenDaysFrom])

	const isLoading = getSevenDaysReservableSlotsChunksResult.loading

	return {
		getChunksAndExtend,
		chunks,
		isLoading,
		sevenDaysFrom,
		setSevenDaysFrom,
	}
}

const useChunksV2 = (
	cancelID?: string,
	fetchedReservableSlots?: (
		startTime: Date,
		reqId: string,
		tookMilliseconds: number,
		needAnyReservableEquipmentToCreateReservation: boolean,
		needSameEquipmentAsPreviousToCreateReservation: boolean,
		staggeredSchedulingEnabled: boolean,
		cacheReqId?: string
	) => void
): ChunksV2ReturnType => {
	const [chunks, setChunks] = useState<
		GetSevenDaysReservableSlotsChunksV2Query["sevenDaysReservableSlotsChunksV2"]["chunks"]
	>([])
	// 追加で枠を取得しにいくと、どんどん append されていく
	const recommendedSlots = useRef<
		GetSevenDaysReservableSlotsChunksV2Query["sevenDaysReservableSlotsChunksV2"]["recommendedSlots"]
	>([])
	const [
		needAnyReservableEquipmentToCreateReservation,
		setNeedAnyReservableEquipmentToCreateReservation,
	] = useState(false)
	const [
		needSameEquipmentAsPreviousToCreateReservation,
		setNeedSameEquipmentAsPreviousToCreateReservation,
	] = useState(false)
	const [staggeredSchedulingEnabled, setStaggeredSchedulingEnabled] =
		useState(false)
	const fetchingFromDatesRef = useRef<Set<string>>(new Set())
	const [sevenDaysFrom, setSevenDaysFrom] = useState(() => new Date())

	const queryTimingCallback = useCallback(
		(timing: QueryTiming) => {
			if (timing.requestId && timing.variables && fetchedReservableSlots) {
				if (timing.variables.param?.fromDate) {
					// response から cacheReqId を取得
					const cacheReqId =
						timing.response?.data?.sevenDaysReservableSlotsChunksV2?.cacheReqId
					const needAny =
						timing.response?.data?.sevenDaysReservableSlotsChunksV2
							?.needAnyReservableEquipmentToCreateReservation
					const needSame =
						timing.response?.data?.sevenDaysReservableSlotsChunksV2
							?.needSameEquipmentAsPreviousToCreateReservation
					const staggered =
						timing.response?.data?.sevenDaysReservableSlotsChunksV2
							?.staggeredSchedulingEnabled
					if (
						needAny !== undefined &&
						needSame !== undefined &&
						staggered !== undefined
					) {
						fetchedReservableSlots(
							new Date(timing.variables.param?.fromDate),
							timing.requestId,
							timing.tookMilliseconds,
							needAny,
							needSame,
							staggered,
							cacheReqId
						)
					}
				}
			}
		},
		[fetchedReservableSlots]
	)

	const [
		getSevenDaysReservableSlotsChunksV2,
		getSevenDaysReservableSlotsChunksV2Result,
	] = useLazyQuery(SevenDaysReservableSlotsChunksV2, {
		fetchPolicy: "no-cache",
	})
	const canFetch = useCallback((fromDateStr: string) => {
		return !fetchingFromDatesRef.current.has(fromDateStr)
	}, [])

	const getChunksAndExtend = useCallback(
		(fromDate: Date, cancelID?: string) => {
			if (!cancelID) return

			const fromDateStr = formatJstDateUntilDayByHyphen(fromDate)

			if (!canFetch(fromDateStr)) return

			fetchingFromDatesRef.current.add(fromDateStr)

			const context: ApolloContext = {
				fetchOptions: {
					requestId: uuidv4(),
					skipLoading: true,
				},
				queryTimingCallback,
			}

			getSevenDaysReservableSlotsChunksV2({
				variables: { param: { fromDate: fromDateStr, cancelId: cancelID } },
				context,
			})
				.then((res) => {
					const gotChunks =
						res.data?.sevenDaysReservableSlotsChunksV2.chunks ?? []
					setChunks((prev) => [...prev, ...gotChunks])
					recommendedSlots.current.push(
						...(res.data?.sevenDaysReservableSlotsChunksV2.recommendedSlots ??
							[])
					)
					const needAny =
						res.data?.sevenDaysReservableSlotsChunksV2
							.needAnyReservableEquipmentToCreateReservation ?? false
					setNeedAnyReservableEquipmentToCreateReservation(needAny)
					const needSame =
						res.data?.sevenDaysReservableSlotsChunksV2
							.needSameEquipmentAsPreviousToCreateReservation ?? false
					setNeedSameEquipmentAsPreviousToCreateReservation(needSame)
					const staggered =
						res.data?.sevenDaysReservableSlotsChunksV2
							.staggeredSchedulingEnabled ?? false
					setStaggeredSchedulingEnabled(staggered)
				})
				.catch((error) => {
					fetchingFromDatesRef.current.delete(fromDateStr)
					console.error(error)
					throw error
				})
		},
		[canFetch, getSevenDaysReservableSlotsChunksV2]
	)

	useEffect(() => {
		getChunksAndExtend(sevenDaysFrom, cancelID)
	}, [cancelID, getChunksAndExtend, sevenDaysFrom])

	const resetAndRefetch = useCallback(
		(cancelID: string) => {
			setChunks([])
			fetchingFromDatesRef.current.clear()
			getChunksAndExtend(sevenDaysFrom, cancelID)
		},
		[getChunksAndExtend, sevenDaysFrom]
	)

	const isLoading = getSevenDaysReservableSlotsChunksV2Result.loading

	return {
		getChunksAndExtend,
		chunks,
		recommendedSlots: recommendedSlots.current,
		needAnyReservableEquipmentToCreateReservation,
		needSameEquipmentAsPreviousToCreateReservation,
		staggeredSchedulingEnabled,
		isLoading,
		sevenDaysFrom,
		setSevenDaysFrom,
		resetAndRefetch,
	}
}

export const getCompletedPath = (cancelID: string, version: number) => {
	if (version === PAGE_VERSION_1) {
		return "/rereservation/complete/cancel/" + cancelID
	} else if (version === PAGE_VERSION_2) {
		return "/rereservation_v2/complete/cancel/" + cancelID
	} else {
		throw new Error("Invalid version")
	}
}
